/**
 * Listing expiration status helper.
 * Returns a tri-state object for rendering lifecycle badges/labels.
 */

const MS_PER_DAY = 86400000;
const EXPIRING_SOON_DAYS = 3;

/**
 * @param {Object} listing   raw listing from /api/listings
 * @param {Function} [t]     i18n translator (optional; falls back to FR)
 * @returns {{status: 'active'|'expiring'|'expired', label: string, daysLeft: number|null, severity: 'info'|'warn'|'danger'}}
 */
export const getListingStatus = (listing, t) => {
  const tr = t || ((k, f) => f);
  const now = Date.now();

  // Manual deletion (highest precedence — overrides everything else)
  if (listing?.status === 'deleted') {
    return {
      status: 'deleted',
      severity: 'danger',
      daysLeft: null,
      label: tr('listing.status.deleted', 'Deleted'),
    };
  }

  // Sold takes high precedence (terminal state)
  if (listing?.status === 'sold') {
    return {
      status: 'sold',
      severity: 'success',
      daysLeft: null,
      label: tr('listing.status.sold', '✅ Vendu'),
    };
  }

  if (listing?.status === 'hidden') {
    return {
      status: 'hidden',
      severity: 'warn',
      daysLeft: null,
      label: tr('listing.status.hidden', '🙈 Masqué'),
    };
  }

  // Explicit expired status takes precedence
  if (listing?.status === 'expired') {
    return {
      status: 'expired',
      severity: 'danger',
      daysLeft: 0,
      label: tr('listing.status.expired', '⛔ Expiré'),
    };
  }

  const expiresAt = listing?.expires_at;
  if (!expiresAt) {
    // No expiration set → treat as active with the usual "Published X ago" label
    return { status: 'active', severity: 'info', daysLeft: null, label: '' };
  }

  const expMs = new Date(expiresAt).getTime();
  const daysLeft = Math.max(0, Math.ceil((expMs - now) / MS_PER_DAY));

  if (expMs <= now) {
    return {
      status: 'expired',
      severity: 'danger',
      daysLeft: 0,
      label: tr('listing.status.expired', '⛔ Expiré'),
    };
  }

  if (daysLeft <= EXPIRING_SOON_DAYS) {
    return {
      status: 'expiring',
      severity: 'warn',
      daysLeft,
      label: tr('listing.status.expiringSoon', `⚠ Expire dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`, { count: daysLeft }),
    };
  }

  return { status: 'active', severity: 'info', daysLeft, label: '' };
};

export const isExpired = (listing) => getListingStatus(listing).status === 'expired';
export const isExpiringSoon = (listing) => getListingStatus(listing).status === 'expiring';
